import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, cores, statusInfo, isHoje, fromInputDate } from "../../constants";

export default function ModalDetalhes({ tarefa, onFechar, onGerenciar, perfilUsuario, onAtualizar }) {
  const s = statusInfo[tarefa.status];
  const isOperacional = ["adm", "gestor", "operador"].includes(perfilUsuario);
  const isDono = perfilUsuario === "dono";
  const hoje = isHoje(tarefa.prazo);
  const prazoIndefinido = !tarefa.prazo || tarefa.prazo === "A definir";
  const [definindoPrazo, setDefinindoPrazo] = useState(false);
  const [novoPrazo, setNovoPrazo] = useState("");
  const [salvandoPrazo, setSalvandoPrazo] = useState(false);

  const salvarPrazo = async () => {
    if (!novoPrazo) return;
    setSalvandoPrazo(true);
    try {
      await axios.patch(`${API}/tarefas/${tarefa.id}/editar`, { prazo: fromInputDate(novoPrazo), usuario: perfilUsuario });
      onAtualizar();
      onFechar();
    } catch { alert("Erro ao salvar prazo."); }
    setSalvandoPrazo(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
        className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl p-5 w-full sm:max-w-md">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 mr-3">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s?.bg} ${s?.text}`}>{s?.emoji} {s?.label}</span>
              {hoje && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">📅 Hoje</span>}
              <span className="text-xs font-medium" style={{ color: cores[tarefa.prioridade] }}>● {tarefa.prioridade}</span>
            </div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-snug">{tarefa.titulo}</h2>
          </div>
          <button onClick={onFechar} className="text-gray-400 text-xl min-h-11 min-w-11 flex items-center justify-center rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800">×</button>
        </div>

        <div className="flex gap-2 flex-wrap mb-3">
          <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-lg">🌾 {tarefa.cultura}</span>
          {tarefa.area && <span className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-lg">📍 {tarefa.area}</span>}

          {/* Prazo — clicável se "A definir" */}
          {prazoIndefinido && isOperacional ? (
            <button onClick={() => setDefinindoPrazo(true)}
              className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-700 font-semibold hover:bg-amber-100 transition">
              📅 A definir — toque para definir
            </button>
          ) : (
            <span className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-lg">📅 {tarefa.prazo}</span>
          )}

          {tarefa.delegado_para_nome && <span className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-1 rounded-lg">👷 {tarefa.delegado_para_nome}</span>}
        </div>

        {/* Campo para definir prazo inline */}
        {definindoPrazo && (
          <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">📅 Definir prazo</p>
            <div className="flex gap-2">
              <input type="date" value={novoPrazo} onChange={e => setNovoPrazo(e.target.value)}
                className="flex-1 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-700 dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 min-h-11" />
              <button onClick={salvarPrazo} disabled={salvandoPrazo || !novoPrazo}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-semibold px-4 py-2 rounded-xl text-xs transition min-h-11">
                {salvandoPrazo ? "..." : "Salvar"}
              </button>
              <button onClick={() => setDefinindoPrazo(false)} className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-xs text-gray-500 min-h-11">✕</button>
            </div>
          </div>
        )}

        {tarefa.descricao && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 mb-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">📋 Descrição / Receita</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{tarefa.descricao}</p>
          </div>
        )}

        {tarefa.criado_por_perfil === "consultor" && tarefa.criado_por_nome && (
          <div className="text-xs text-green-700 dark:text-green-400 mb-3 font-medium">🌿 Recomendado por: {tarefa.criado_por_nome}</div>
        )}

        <div className="flex gap-2">
          <button onClick={onFechar} className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 min-h-11">Fechar</button>
          {(isOperacional || isDono) && (
            <button onClick={onGerenciar} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl text-sm transition min-h-11">
              {isDono ? "📋 Ver histórico" : "⚡ Gerenciar"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
